import { DownloadSimpleIcon, PlusIcon, XIcon } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import useSWR from 'swr';

import { PageHeader } from '../../../components/PageHeader';
import { Pagination } from '../../../components/pagination/Pagination';
import { ITableHeader, Table, TableSortOrder } from '../../../components/table/Table';
import { formatDate } from '../../../utils/date';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { LinkButton } from '../../../components/button/ButtonLink';
import { PageHeading } from 'components/PageHeading';
import { FilterField, FilterPanel, ListPage } from 'components/page-templates/ListPage';
import { Input } from '../../../components/input/Input';
import { SimpleSelect } from '../../../components/select/SimpleSelect';
import { BaseButton } from '../../../components/button/BaseButton';
import { Button } from '../../../components/button/Button';
import { getDisplayError } from '../../../utils/get-display-error';
import { fetchClaims, ClaimStatusType } from '../models';
import { exportClaimsToExcel } from './export-claims';
import { CLAIM_STATUS_OPTIONS, CLAIM_STATUS_LABELS } from '../constants';

type ClaimSortBy = 'createdAt' | 'status' | 'amount';

const CLAIMS_HEADERS: ITableHeader[] = [
  {
    id: 'counterParty',
    name: 'Tegenpartij',
  },
  {
    id: 'supplier',
    name: 'Onderaannemer',
  },
  {
    id: 'amount',
    name: 'Bedrag',
    sortable: true,
  },
  {
    id: 'status',
    name: 'Status',
    sortable: true,
  },
  {
    id: 'info',
    name: 'Info',
    width: '200px',
  },
  {
    id: 'createdAt',
    name: 'Aangemaakt op',
    sortable: true,
  },
];

const PAGE_SIZE = 50;

const formatAmount = (cents: number) => {
  return `€ ${(cents / 100).toFixed(2)}`;
};

const ClaimsPage = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<{ key: string; name: string } | null>(null);
  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState<ClaimSortBy | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<TableSortOrder | undefined>(undefined);

  const { data, error, isLoading, mutate } = useSWR(
    ['claims', search, statusFilter?.key, offset, sortBy, sortOrder],
    () =>
      fetchClaims({
        search: search || undefined,
        status: (statusFilter?.key as ClaimStatusType) || undefined,
        limit: PAGE_SIZE + 1,
        offset,
        sortBy,
        sortOrder,
      }),
  );

  const hasNext = (data?.length ?? 0) > PAGE_SIZE;
  const claims = useMemo(() => (data ?? []).slice(0, PAGE_SIZE), [data]);

  const handleExportExcel = async () => {
    try {
      const allClaims = await fetchClaims({
        search: search || undefined,
        status: (statusFilter?.key as ClaimStatusType) || undefined,
      });
      await exportClaimsToExcel(allClaims);
      toast.success('Export gestart');
    } catch (err) {
      toast.error('Export mislukt: ' + getDisplayError(err));
    }
  };

  const activeFilterCount = [search.trim(), statusFilter].filter(Boolean).length;

  return (
    <>
      <PageHeader title="Claims" />

      <ListPage
        heading={
          <PageHeading
            noMarginBottom
            leftSide={
              <Breadcrumb
                parentItem={{
                  name: 'Financieel',
                  to: '/internal/finance',
                }}
                currentItem="Claims"
              />
            }
            rightSide={
              <>
                <BaseButton
                  color="secondary"
                  iconLeft={<DownloadSimpleIcon className="button-icon" />}
                  onClick={handleExportExcel}
                  disabled={claims.length === 0}
                >
                  Exporteer naar Excel
                </BaseButton>
                <LinkButton to="new" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
                  Nieuwe claim
                </LinkButton>
              </>
            }
          />
        }
        panel
        filters={
          <FilterPanel
            flush
            activeCount={activeFilterCount}
            actions={
              <Button
                size={8}
                color="secondary"
                iconLeft={<XIcon className="button-icon" />}
                onTrigger={() => {
                  setSearch('');
                  setStatusFilter(null);
                  setOffset(0);
                }}
              >
                Wis alle filters
              </Button>
            }
          >
            <FilterField label="Zoek">
              <Input
                type="text"
                value={search}
                onChange={(val) => {
                  setSearch(val);
                  setOffset(0);
                }}
                placeholder="Zoek op tegenpartij..."
              />
            </FilterField>

            <FilterField label="Status">
              <SimpleSelect
                selectedItem={statusFilter}
                items={CLAIM_STATUS_OPTIONS}
                onSelect={(item) => {
                  setStatusFilter(item);
                  setOffset(0);
                }}
                isOptional={true}
              />
            </FilterField>
          </FilterPanel>
        }
        pagination={
          <Pagination
            hasPrevious={offset > 0}
            previous={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            hasNext={hasNext}
            next={() => setOffset(offset + PAGE_SIZE)}
            isFetching={isLoading}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={CLAIMS_HEADERS}
          data={claims}
          error={error}
          onRetry={() => mutate()}
          isLoading={isLoading}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={(id, order) => {
            setSortBy(id as ClaimSortBy | undefined);
            setSortOrder(order);
            setOffset(0);
          }}
          mapData={(claim) => {
            return [
              <Link to={`${claim.id}`} className="link-text">
                {claim.counterParty}
              </Link>,
              claim.supplierName || '-',
              formatAmount(claim.amount),
              CLAIM_STATUS_LABELS[claim.status] || claim.status,
              <div className="max-w-xs truncate" title={claim.info}>
                {claim.info || '-'}
              </div>,
              formatDate(claim.createdAt),
            ];
          }}
        />
      </ListPage>
    </>
  );
};

export default ClaimsPage;
